import React, { useState, useCallback, useRef } from 'react';
import { AllGenerationsViewer } from '../components/AllGenerationsViewer';
import { useAuth } from '../contexts/AuthContext';
import { userGalleryService, UploadResult } from '../lib/userGalleryService';
import { ImageOptimizer } from '../lib/imageOptimizer';

interface UploadState {
  isUploading: boolean;
  progress: number;
  result?: UploadResult;
  dragOver: boolean;
}

export const YourGallery: React.FC = () => {
  const { user } = useAuth();
  const [uploadState, setUploadState] = useState<UploadState>({
    isUploading: false,
    progress: 0,
    dragOver: false
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = useCallback(async (file: File) => {
    if (!user?.id) {
      alert('Please sign in to upload images.');
      return;
    }

    // Validate file
    if (!ImageOptimizer.isValidImageFile(file)) {
      alert('Unsupported file format. Please upload JPG, JPEG, PNG, HEIC, or HEIF images.');
      return;
    }

    if (!ImageOptimizer.validateFileSize(file, 50)) {
      alert('File size too large. Please upload images smaller than 50MB.');
      return;
    }

    setUploadState({
      isUploading: true,
      progress: 0,
      dragOver: false
    });

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadState(prev => ({
          ...prev,
          progress: Math.min(prev.progress + 10, 90)
        }));
      }, 200);

      const result = await userGalleryService.uploadUserImage(file, user.id);
      
      clearInterval(progressInterval);
      
      setUploadState({
        isUploading: false,
        progress: 100,
        result,
        dragOver: false
      });

      // Clear the result after 5 seconds
      setTimeout(() => {
        setUploadState(prev => ({
          ...prev,
          result: undefined,
          progress: 0
        }));
      }, 5000);

    } catch (error) {
      setUploadState({
        isUploading: false,
        progress: 0,
        result: {
          success: false,
          error: error instanceof Error ? error.message : 'Upload failed'
        },
        dragOver: false
      });
    }
  }, [user?.id]);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Reset input value so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFileUpload]);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  }, [handleFileUpload]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setUploadState(prev => ({ ...prev, dragOver: true }));
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setUploadState(prev => ({ ...prev, dragOver: false }));
  }, []);

  const openFileDialog = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🖼️ Your Gallery
          </h1>
          <p className="text-gray-600 mb-6">
            Upload your own images and view all your AI-generated renovations, moodboards, and touchups in one place.
          </p>
        </div>

        {/* Upload Area */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            📤 Upload Images
          </h2>
          <p className="text-gray-600 mb-4">
            Upload images to your gallery. They'll be optimized to 1024px on the longest side and available for use in all AI tools.
          </p>

          {/* Drag and Drop Area */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={openFileDialog}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200 ${
              uploadState.dragOver
                ? 'border-blue-500 bg-blue-50'
                : uploadState.isUploading
                ? 'border-gray-300 bg-gray-50'
                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />

            {uploadState.isUploading ? (
              <div className="space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <div className="space-y-2">
                  <p className="text-gray-600">Processing your image...</p>
                  <div className="w-full bg-gray-200 rounded-full h-2 max-w-xs mx-auto">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadState.progress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-500">{uploadState.progress}%</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <svg className="w-16 h-16 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <div>
                  <p className="text-lg font-medium text-gray-600">
                    {uploadState.dragOver ? 'Drop your image here' : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Support: JPG, JPEG, PNG, HEIC, HEIF (Max 50MB)
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Images will be optimized to 1024px on the longest side
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Upload Result */}
          {uploadState.result && (
            <div className={`mt-4 p-4 rounded-lg ${
              uploadState.result.success
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center space-x-3">
                {uploadState.result.success ? (
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                <div className="flex-1">
                  {uploadState.result.success ? (
                    <div>
                      <p className="text-green-800 font-medium">Upload successful!</p>
                      {uploadState.result.upload && (
                        <p className="text-green-700 text-sm">
                          {uploadState.result.upload.originalFilename} optimized and saved to your gallery.
                          {uploadState.result.upload.compressionRatio && 
                            ` Reduced by ${uploadState.result.upload.compressionRatio.toFixed(1)}%`
                          }
                        </p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <p className="text-red-800 font-medium">Upload failed</p>
                      <p className="text-red-700 text-sm">{uploadState.result.error}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <AllGenerationsViewer />
      </div>
    </div>
  );
};